import { useState, useEffect, useCallback } from 'react';
import staticDataService from '../services/staticDataService';
import { StaticGridData, UseStaticGridDataReturn } from '../types/staticTypes';

export const useStaticGridData = (
  championName?: string,
  opponentName?: string,
  batchSize: number = 6
): UseStaticGridDataReturn => {
  const [gridData, setGridData] = useState<StaticGridData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingNextBatch, setLoadingNextBatch] = useState<boolean>(false);

  const loadNextBatchAutomatically = useCallback(async (currentData: StaticGridData, opponent?: string) => {
    if (!championName || !currentData.metadata.batchInfo?.nextBatchStart) {
      return;
    }

    try {
      setLoadingNextBatch(true);
      
      const nextStartLevel = currentData.metadata.batchInfo.nextBatchStart;
      console.log(`[useStaticGridData] Auto-loading next batch starting at level ${nextStartLevel}`);
      
      const nextBatchData = await staticDataService.fetchNextBatch(
        championName,
        nextStartLevel,
        opponent,
        batchSize
      );
      
      setGridData(nextBatchData);
      
      // Continue loading if there are more batches
      if (nextBatchData.metadata.batchInfo && !nextBatchData.metadata.batchInfo.isComplete) {
        loadNextBatchAutomatically(nextBatchData, opponent);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading next batch automatically:', err);
    } finally {
      setLoadingNextBatch(false);
    }
  }, [championName, batchSize]);

  const fetchInitialBatch = useCallback(async (opponent?: string) => {
    if (!championName) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`[useStaticGridData] Fetching initial batch for ${championName} vs ${opponent || 'default'}`);
      
      // Fetch first batch
      const data = await staticDataService.fetchComboGrid(championName, opponent, batchSize);
      
      setGridData(data);
      
      // Automatically start loading subsequent batches if not complete
      if (data.metadata.batchInfo && !data.metadata.batchInfo.isComplete) {
        loadNextBatchAutomatically(data, opponent);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching grid data:', err);
    } finally {
      setLoading(false);
    }
  }, [championName, batchSize, loadNextBatchAutomatically]);

  const loadNextBatchManually = useCallback(async (opponent?: string) => {
    if (!championName || !gridData?.metadata.batchInfo?.nextBatchStart) {
      return;
    }

    try {
      setLoadingNextBatch(true);
      
      const nextStartLevel = gridData.metadata.batchInfo.nextBatchStart;
      console.log(`[useStaticGridData] Manually loading next batch starting at level ${nextStartLevel}`);
      
      const nextBatchData = await staticDataService.fetchNextBatch(
        championName,
        nextStartLevel,
        opponent,
        batchSize
      );
      
      setGridData(nextBatchData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading next batch manually:', err);
    } finally {
      setLoadingNextBatch(false);
    }
  }, [championName, gridData, batchSize]);

  const refetch = async (opponent?: string) => {
    await fetchInitialBatch(opponent);
  };

  const fetchCurrentGrid = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[useStaticGridData] Fetching current grid state');
      
      const data = await staticDataService.fetchCurrentGrid();
      setGridData(data);
      
      console.log('[useStaticGridData] ✅ Current grid loaded successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching current grid:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialBatch(opponentName);
  }, [fetchInitialBatch, opponentName]); // Now properly includes fetchInitialBatch

  return {
    gridData,
    loading,
    error,
    loadingNextBatch,
    refetch,
    fetchCurrentGrid,
    loadNextBatch: loadNextBatchManually
  };
};

export default useStaticGridData; 